<script lang="ts" setup>
const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  controls: {
    type: Boolean,
    default: true,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  muted: {
    type: Boolean,
    default: false,
  },
  loop: {
    type: Boolean,
    default: false,
  },
  playsinline: {
    type: Boolean,
    default: true,
  },
  width: {
    type: String,
    default: '',
  },
  height: {
    type: String,
    default: '',
  },
})

const { public: config } = useRuntimeConfig()

const autoplay = toRefs(props).autoplay

const video = ref<HTMLVideoElement>()

watch(autoplay, (newVal) => {
  if (newVal) {
    video.value?.play()
  }
  else {
    video.value?.pause()
  }
})

const type = computed(() => {
  if (!props.src) {
    return 'unknown'
  }

  switch (true) {
    case props.src.endsWith('.webm'):
      return 'video/webm'
    case props.src.endsWith('.mp4'):
      return 'video/mp4'
    case props.src.includes('you') && props.src.includes('/shorts/'):
      return 'youtube-shorts'
    case props.src.includes('youtube'):
    case props.src.includes('youtu.be'):
      return 'youtube'
    case props.src.includes('vimeo'):
      return 'vimeo'
    case props.src.includes('tiktok'):
      return 'tiktok'
  }

  return 'unknown'
})

const url = computed(() => {
  if (!props.src) {
    return ''
  }

  switch (true) {
    case props.src.includes('youtube'):
    case props.src.includes('youtu.be'):
      return getYoutubeUrl(props.src)
    case props.src.includes('vimeo'):
      return getVimeoUrl(props.src)
    case props.src.includes('tiktok'):
      return getTiktokUrl(props.src)
  }

  return props.src
})

const ratios = computed(() => {
  const ratio = {}

  ratio['--ratio'] = `${props.width || 16} / ${props.height || 9}`

  return ratio
})

function getYoutubeUrl(src: string): string {
  let code = ''
  const params = `?wmode=opaque&rel=0&autoplay=${props.autoplay ? '1' : '0'}&controls=${
    props.controls ? '1' : '0'
  }&loop=${props.loop ? '1' : '0'}&mute=${props.muted ? '1' : '0'}&playsinline=1&enablejsapi=0&origin=${encodeURIComponent(config.baseDomain)}`

  if (src.includes('youtube.com/embed')) {
    return src?.split('?')?.[0] + params
  }

  if (src.includes('youtube.com/shorts')) {
    code = src.split('/shorts/')?.[1]
    return `https://www.youtube.com/embed/${code}${params}`
  }

  if (src.includes('watch')) {
    const urlSearchParams = new URLSearchParams(src.split('?')?.[1] || '')
    code = urlSearchParams.get('v') || ''
    return `https://www.youtube.com/embed/${code}${params}`
  }

  code = src?.replace('://', '').split('/')?.[1] || ''
  return `https://www.youtube.com/embed/${code}${params}`
}

function getVimeoUrl(src: string): string {
  let code = ''
  let params = ''

  params = `?autoplay=${props.autoplay ? '1' : '0'}&controls=${
    props.controls ? '1' : '0'
  }&loop=${props.loop ? '1' : '0'}&muted=${props.muted ? '1' : '0'}&autopause=${props.autoplay ? '0' : '1'}`

  if (src.includes('player.vimeo')) {
    return src?.split('?')?.[0] + params
  }

  code = src?.split('?')?.[0]?.replace('://', '').split('/')?.[1] || ''
  return `https://player.vimeo.com/video/${code}${params}`
}

function getTiktokUrl(src: string): string {
  let code = '6718335390845095173'
  // const params = `?autoplay=${props.autoplay ? '1' : '0'}&controls=${
  //   props.controls ? '1' : '0'
  // }&loop=${props.loop ? '1' : '0'}&mute=${props.muted ? '1' : '0'}`

  code = src.split('/video/')?.[1] || ''
  return `https://www.tiktok.com/embed/v2/${code}`
}
</script>

<template>
  <iframe
    v-if="src && ['tiktok', 'youtube-shorts', 'youtube', 'vimeo'].includes(type)"
    :src="url"
    :class="`video video--${type}`"
    :title="`${type} video player`"
    frameborder="0"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
    allowfullscreen
    crossorigin
    referrerpolicy="strict-origin-when-cross-origin"
    loading="lazy"
  />
  <video
    v-else-if="src && ['video/webm', 'video/mp4'].includes(type)"
    ref="video"
    class="video video--web"
    :style="ratios"
    :controls="controls"
    :autoplay="autoplay"
    :muted="muted"
    :loop="loop"
    :playsinline="playsinline"
    :preload="autoplay ? 'auto' : 'metadata'"
    :width="width"
    :height="height"
  >
    <source
      :src="url"
      :type="type"
      crossorigin
    >
    Download <a :href="url">{{ url }}</a>
  </video>
  <p v-else>
    Unknown video type {{ type }}: {{ src }}
  </p>
</template>

<style>
@import url("~layers/components/Ui/Video.css");
</style>
